import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageHeader";
import { useDash, ROLE_LABELS } from "./ctx";
import { NotificationPrefs } from "@/components/NotificationPrefs";
import { ResidentLinkCard } from "@/components/ResidentLinkCard";
import { UserCog, Bell, KeyRound, SlidersHorizontal } from "lucide-react";
import { ContactVerificationCard } from "@/components/PhoneVerify";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PasswordForm } from "@/components/PasswordForm";

// Personal account settings — reached from the avatar menu (bottom-left). This is the
// user's OWN space, distinct from the community "Settings" page. Home for how each staff
// member is notified (email / text / call) + verifying their mobile.
export default function Account() {
  const { user } = useAuth();
  // Badge shows the seat you actually hold — inside a community an Administrator ACTS as its manager
  // (that is `role`), but they are still the firm's Administrator.
  const { role, actualRole, canCustomizeMenu, openCustomizeMenu } = useDash();
  const { hash } = useLocation();
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  // Deep-links from the top-right bell / Reports land on the notification prefs, not the top.
  // NotificationPrefs loads async and grows the page, so retry briefly until the scroll sticks —
  // but stop early if the user scrolls themselves.
  useEffect(() => {
    const target = { "#notifications": "account-notifications", "#verify": "account-verify", "#password": "account-password" }[hash];
    if (!target) return;
    let tries = 0, timer, cancelled = false;
    const cleanup = () => {
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
    };
    const stop = () => { cancelled = true; clearTimeout(timer); cleanup(); };
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (++tries < 8) timer = setTimeout(tick, 300); else cleanup();
    };
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    timer = setTimeout(tick, 100);
    return () => { clearTimeout(timer); cleanup(); };
  }, [hash]);

  return (
    <div className="max-w-3xl space-y-6" data-testid="account-page">
      <div>
        <PageTitle title={<><UserCog className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Account settings</>} about="Your personal preferences — how you sign in and how Ask Redline reaches you." />
      </div>

      {/* Who you are */}
      <div data-testid="account-identity" className="rounded-2xl border border-sand-200 bg-white px-4 py-4 sm:px-5 flex items-center gap-4">
        <div className="h-12 w-12 rounded-full bg-redline-500 text-white flex items-center justify-center text-lg font-bold shrink-0">{initial}</div>
        <div className="min-w-0">
          <div className="font-heading font-bold text-ink truncate">{user?.name || "You"}</div>
          <div className="text-sm text-muted-foreground truncate">{user?.email}</div>
        </div>
        <span className="ml-auto shrink-0 rounded-full bg-ink/5 px-3 py-1 text-xs font-semibold text-ink/70">{ROLE_LABELS[actualRole || role] || actualRole || role}</span>
      </div>

      {/* Can we reach you? Email is proven by signing in; the mobile needs a texted code once. */}
      <ContactVerificationCard user={user} />

      {/* Your resident profile — link staff + resident identities (board members / managers who are also residents) */}
      {["hoa_manager", "board_member"].includes(role) && <ResidentLinkCard />}

      {/* Sign-in — the password is how you get in from any device */}
      {user?.email && (
        <div id="account-password" data-testid="account-password" className="rounded-2xl border border-sand-200 bg-white p-4 sm:p-6 scroll-mt-20">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="h-5 w-5 text-redline-500 shrink-0" strokeWidth={1.75} />
            <h2 className="font-heading text-lg font-bold text-ink">{user?.has_password ? "Password" : "Set a password"}</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {user?.has_password
              ? <>You sign in with <span className="font-semibold text-ink">{user.email}</span> and your password. Google sign-in works too when the accounts match.</>
              : <>Your account doesn't have a password yet. Set one and you can sign in with <span className="font-semibold text-ink">{user.email}</span> from any device, no emailed links needed.</>}
          </p>
          <div className="max-w-sm">
            <PasswordForm onDone={() => toast.success(user?.has_password ? "Password changed." : "Password set — sign in with it from now on.")} testPrefix="account-password-form" />
          </div>
        </div>
      )}

      {/* Notifications */}
      <div id="account-notifications" data-testid="account-notifications" className="rounded-2xl border border-sand-200 bg-white p-4 sm:p-6 scroll-mt-20">
        <div className="flex items-center gap-2 mb-1">
          <Bell className="h-5 w-5 text-redline-500 shrink-0" strokeWidth={1.75} />
          <h2 className="font-heading text-lg font-bold text-ink">Notifications</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Choose how you hear about each kind of activity, and verify your mobile so text and call alerts can reach you.</p>
        <NotificationPrefs hideDone hideContact />
      </div>

      {/* Sidebar layout — a personal preference, so it lives here rather than as a menu item of its own */}
      {canCustomizeMenu && (
        <div data-testid="account-menu-layout" className="rounded-2xl border border-sand-200 bg-white p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <SlidersHorizontal className="h-5 w-5 text-redline-500 shrink-0" strokeWidth={1.75} />
              <h2 className="font-heading text-lg font-bold text-ink">Sidebar menu</h2>
            </div>
            <p className="text-sm text-muted-foreground">Choose which sections show and in what order, or go back to the classic layout. Applies to everyone who uses this console.</p>
          </div>
          <button type="button" data-testid="customize-menu-btn" onClick={openCustomizeMenu}
            className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-semibold text-ink hover:border-ink transition-colors">
            <SlidersHorizontal className="h-4 w-4" /> Organize menu
          </button>
        </div>
      )}
    </div>
  );
}
